import { ElementoBase } from '@/enum/elemento_base';

/**
 * Calcula el ancho resultante de un proceso de extrusión a partir de sus bobinas.
 * Misma lógica que ExtrusionBobinasResumen.vue:
 *  - Sin aprovechar el ancho máximo: suma de los anchos de las bobinas.
 *  - Aprovechando el ancho máximo: cuántas bobinas entran en el ancho máximo de la
 *    máquina (floor(AnchoMaximo / anchoPrimeraBobina)) por la suma de anchos.
 *
 * @param {Array<{Ancho:number}>} bobinas
 * @param {object} [opts]
 * @param {boolean} [opts.aprovecharAnchoMaximo=false]
 * @param {number}  [opts.anchoMaximoMaquina=0]
 * @returns {number} ancho total en mm (0 si no hay datos suficientes)
 */
export function calcularAnchoBobinas(
  bobinas,
  { aprovecharAnchoMaximo = false, anchoMaximoMaquina = 0 } = {},
) {
  const lista = Array.isArray(bobinas) ? bobinas : [];
  const sumAnchosBobinas = lista.reduce((total, bobina) => total + (Number(bobina?.Ancho) || 0), 0);

  if (!aprovecharAnchoMaximo) return sumAnchosBobinas;

  const anchoBobina = Number(lista[0]?.Ancho) || 0;
  const anchoMaximo = Number(anchoMaximoMaquina) || 0;
  const bobinasEnElAncho =
    anchoBobina > 0 && anchoMaximo > 0 ? Math.floor(anchoMaximo / anchoBobina) : 0;

  return bobinasEnElAncho * sumAnchosBobinas;
}

/**
 * Devuelve una NUEVA lista de elementos con AnchoMaterial (y ErrorAnchoMaterial)
 * calculados desde las bobinas de extrusión, sin mutar la lista original.
 *
 * Solo aplica cuando el elemento base es EXTRUSION; en cualquier otro caso devuelve
 * la lista recibida sin cambios. El backend no deriva el ancho de extrusión desde las
 * bobinas: espera AnchoMaterial en el elemento.
 *
 * Compartido entre prices_edit.vue y work_order_edit.vue.
 *
 * @param {Array} elementos - elementos con Secciones[].ElementoProceso[]
 * @param {number} codigoElementoBase - ElementoTipoSeleccionado.CodigoElementoBase
 * @returns {Array} nueva lista de elementos (los elementos con extrusión son copias)
 */
export function conAnchoMaterialExtrusion(elementos, codigoElementoBase) {
  const lista = Array.isArray(elementos) ? elementos : [];
  if (codigoElementoBase !== ElementoBase.EXTRUSION) return lista;

  return lista.map((elemento) => {
    const procesos = (elemento?.Secciones || []).flatMap(
      (seccion) => seccion?.ElementoProceso || [],
    );
    const procesosConBobinas = procesos.filter((proc) =>
      Array.isArray(proc?.ElementoProcesoBobinas),
    );
    if (procesosConBobinas.length === 0) return elemento;

    // Comportamiento previo: el último proceso con bobinas define el ancho del elemento.
    const proc = procesosConBobinas[procesosConBobinas.length - 1];
    const anchoTotalBobinas = calcularAnchoBobinas(proc.ElementoProcesoBobinas, {
      aprovecharAnchoMaximo: proc.AprovecharAnchoMaximo,
      anchoMaximoMaquina: proc.Recurso?.AnchoMaximo,
    });

    return anchoTotalBobinas > 0
      ? { ...elemento, AnchoMaterial: anchoTotalBobinas, ErrorAnchoMaterial: false }
      : { ...elemento, AnchoMaterial: 0, ErrorAnchoMaterial: true };
  });
}
